/**
 * English | Hinglish switch.
 *
 * Keyboard accessible: a radiogroup with two radios, arrow keys move between
 * them, the active language is announced via aria-checked and shown visually.
 * Switching is instant — no reload, no navigation — so scroll, cart, packs,
 * form values and quiz progress are preserved.
 */

import { useLanguage } from "@/lib/i18n/provider";
import { HINGLISH_ENABLED, LANG_LABELS, type Lang } from "@/lib/i18n/config";
import { cn } from "@/lib/utils";

const ORDER: Lang[] = ["en", "hi-en"];

/**
 * Temporarily hidden while the Hinglish rollout is finished. Everything else
 * (dictionaries, provider, persistence) stays wired — flip this to `true` to
 * bring the switch back in the header.
 */
export const LANGUAGE_TOGGLE_VISIBLE = HINGLISH_ENABLED;

export function LanguageToggle({
  className,
  size = "sm",
}: {
  className?: string;
  size?: "sm" | "md";
}) {
  const { lang, setLang, t } = useLanguage();

  if (!LANGUAGE_TOGGLE_VISIBLE) return null;


  return (
    <div
      role="radiogroup"
      aria-label={t("lang.label")}
      className={cn(
        "inline-flex items-center rounded-full bg-[#F2F3F5] p-0.5",
        className,
      )}
    >
      {ORDER.map((option) => {
        const selected = option === lang;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={t("lang.switchTo", { lang: LANG_LABELS[option] })}
            tabIndex={selected ? 0 : -1}
            onClick={() => setLang(option)}
            onKeyDown={(e) => {
              if (
                e.key === "ArrowRight" ||
                e.key === "ArrowLeft" ||
                e.key === "ArrowUp" ||
                e.key === "ArrowDown"
              ) {
                e.preventDefault();
                const next = ORDER[(ORDER.indexOf(lang) + 1) % ORDER.length]!;
                setLang(next);
              }
            }}
            className={cn(
              "rounded-full font-medium tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E5EFF] focus-visible:ring-offset-1",
              size === "sm" ? "px-2.5 py-1 text-[11.5px]" : "px-3.5 py-1.5 text-[13px]",
              selected
                ? "bg-white text-[#1E5EFF] shadow-[0_1px_3px_rgba(44,44,44,0.12)]"
                : "text-[#2C2C2C]/60 hover:text-[#2C2C2C]",
            )}
          >
            {LANG_LABELS[option]}
          </button>
        );
      })}
    </div>
  );
}
